import { useAppSelector } from 'appState/store'
import { RefreshIntervalsEnum } from 'configs/apiConfigs'
import { env } from 'configs/env'
import { authApi } from 'features/auth/authApi'
import { useCall } from 'features/calls/callsHooks'
import { CallStatusEnum } from 'features/calls/types'
import { PathsEnum } from 'pages/Paths'
import { useEffect, useRef, useState } from 'react'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ENV_ALLOWED_URLS } from 'utils/types'

const STORAGE_KEYS = {
  RESET_IDLE_TIMER: 'RESET_IDLE_TIMER',
  USER_ACTIVITY: 'USER_ACTIVITY',
  STAY_LOGGED_IN: 'STAY_LOGGED_IN',
  LOGOUT: 'LOGOUT',
  ACTIVE_CALL: 'ACTIVE_CALL',
  MODAL_STATE: 'MODAL_STATE',
}

const AutoLogout = () => {
  //const navigate = useNavigate()
  const { logoutTime } = useAppSelector((state) => state.auth)
  const idleTimeout = useRef<NodeJS.Timeout | null>(null)
  const modalTimeout = useRef<NodeJS.Timeout | null>(null)
  const idealLogoutTime = parseInt(logoutTime!) === 0 ? parseInt(env.VITE_IDEAL_LOGOUT_TIME!) : parseInt(logoutTime!) // Set to env.VITE_IDEAL_LOGOUT_TIME if logoutTime is 0
  const idleDuration = (idealLogoutTime - 3) * 60 * 1000 // Timer set from customer settings
  const modalDuration = 3 * 60 * 1000 // 3 minutes
  const [showModal, setShowModal] = useState(false)
  const [countdown, setCountdown] = useState(modalDuration / 1000)

  const { activeCallJobId } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  const formatTime = (milliseconds: number) => {
    const minutes = Math.floor(milliseconds / 60000)
    const seconds = Math.floor((milliseconds % 60000) / 1000)
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  const { data: call } = useCall(activeCallJobId, {
    refreshInterval: RefreshIntervalsEnum.useCallForTalkToPayorRepCalls,
  })

  const resetIdleTimer = (broadcast = true) => {
    if (isLoginPage()) return
    const modalState = JSON.parse(localStorage.getItem(STORAGE_KEYS.MODAL_STATE)!)
    if (modalState) {
      return
    }

    // Clear the current idle timer
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current)
    }

    // Set a new idle timer
    idleTimeout.current = setTimeout(() => {
      setShowModal(true)
      localStorage.setItem(STORAGE_KEYS.MODAL_STATE, 'true')
      startModalCountdown()
    }, idleDuration)

    // Broadcast the reset action to other tabs
    if (broadcast) {
      localStorage.setItem(STORAGE_KEYS.RESET_IDLE_TIMER, JSON.stringify(Date.now()))
    }
  }

  const startModalCountdown = () => {
    const modalStartTime = Date.now() // Get the current time
    const modalEndTime = modalStartTime + modalDuration // Calculate when the timer should end

    // Update the countdown every second
    modalTimeout.current = setInterval(() => {
      const currentTime = Date.now()
      const timeLeft = Math.max(0, modalEndTime - currentTime) // Ensure time doesn't go negative
      setCountdown(timeLeft / 1000) // Update countdown state

      if (timeLeft <= 0) {
        clearInterval(modalTimeout.current!)
        signout(true) // Logout when the countdown ends
      }
    }, 1000)
  }

  const clearModalCountdown = () => {
    if (modalTimeout.current) {
      clearTimeout(modalTimeout.current)
    }
    setCountdown(modalDuration / 1000)
  }

  const stayLoggedIn = () => {
    setShowModal(false)
    localStorage.setItem(STORAGE_KEYS.MODAL_STATE, 'false')
    clearModalCountdown()
    resetIdleTimer()
    localStorage.setItem(STORAGE_KEYS.STAY_LOGGED_IN, JSON.stringify(Date.now())) // Notify other tabs
  }

  const signout = async (AUTO_LOGOUT: boolean | undefined) => {
    clearModalCountdown()
    setShowModal(false)
    localStorage.setItem(STORAGE_KEYS.MODAL_STATE, 'false')
    try {
      if (AUTO_LOGOUT) {
        localStorage.setItem('LOGOUT_REASON', 'AUTO_LOGOUT')
      } else {
        localStorage.setItem('LOGOUT_REASON', 'MANUAL_LOGOUT')
      }
      authApi.signOut('true')
      //navigate(PathsEnum.root)
      //window.location.reload()
      localStorage.setItem(STORAGE_KEYS.LOGOUT, JSON.stringify(Date.now()))
    } catch (error) {
      console.error('Error signing out: ', error)
    }
  }

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.MODAL_STATE, JSON.stringify(showModal))
    if (!showModal) {
      setShowModal(JSON.parse(localStorage.getItem(STORAGE_KEYS.MODAL_STATE) || 'false'))
    }
  }, [showModal])

  const isLoginPage = () => location.pathname === PathsEnum.login

  const handleUserActivity = () => {
    resetIdleTimer()
    // Broadcast user activity to other tabs
    localStorage.setItem(STORAGE_KEYS.USER_ACTIVITY, JSON.stringify(Date.now()))
  }

  useEffect(() => {
    // TODO: Ensure this runs only in environments where auto-logout is enabled
    if (isLoginPage()) return

    const hostname = window.location.hostname

    // Check if the hostname is explicitly allowed in any environment
    const isAllowedEnvironment =
      ENV_ALLOWED_URLS.production.includes(hostname) ||
      ENV_ALLOWED_URLS.staging.includes(hostname) ||
      ENV_ALLOWED_URLS.development.includes(hostname)

    if (!isAllowedEnvironment) {
      return
    }
    // Check for active calls; do not start idle timer if a call is ongoing (Queued or InProgress)
    const isCallActive =
      activeCallJobId && (call?.status === CallStatusEnum.InProgress || call?.status === CallStatusEnum.Queued)

    if (isCallActive) {
      // broadcast active call to other tabs
      localStorage.setItem(STORAGE_KEYS.ACTIVE_CALL, JSON.stringify(Date.now()))
      return // Exit the effect and do not start the timer
    }

    const activityEvents = ['mousemove', 'keypress', 'scroll', 'click']
    activityEvents.forEach((event) => window.addEventListener(event, handleUserActivity))

    // Initialize idle timer on component mount
    resetIdleTimer()

    // Sync behavior across tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === STORAGE_KEYS.RESET_IDLE_TIMER ||
        event.key === STORAGE_KEYS.USER_ACTIVITY ||
        event.key === STORAGE_KEYS.ACTIVE_CALL
      ) {
        resetIdleTimer(false) // Reset timer without broadcasting
      } else if (event.key === STORAGE_KEYS.STAY_LOGGED_IN) {
        stayLoggedIn() // Extend session on other tabs
      } else if (event.key === STORAGE_KEYS.LOGOUT) {
        const LOGOUT_REASON = localStorage.getItem('LOGOUT_REASON')
        signout(LOGOUT_REASON === 'AUTO_LOGOUT' ? true : false) // Log out this tab without rebroadcasting
      } else if (event.key === STORAGE_KEYS.MODAL_STATE) {
        const modalState = JSON.parse(localStorage.getItem(STORAGE_KEYS.MODAL_STATE)!)
        if (!modalState) {
          setShowModal(false)
          clearModalCountdown()
          resetIdleTimer()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      if (idleTimeout.current) clearTimeout(idleTimeout.current)
      if (modalTimeout.current) clearTimeout(modalTimeout.current)
      activityEvents.forEach((event) => window.removeEventListener(event, handleUserActivity))
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [logoutTime, call?.status, activeCallJobId])

  return (
    <>
      {!isLoginPage() && showModal && (
        <ObModal
          show={showModal}
          hideCloseButton={true}
          customSize="small"
          title="Would you like to stay logged in?"
          height="240px"
          radius="rounded"
          modalFooter={
            <>
              <Button
                variant="secondary"
                radius="rounded"
                onClick={() => {
                  signout(false)
                }}
                width="187px"
                size="large"
              >
                No, log out
              </Button>
              <Button variant="primary" width="187px" radius="rounded" onClick={stayLoggedIn} size="large">
                Yes, stay logged in
              </Button>
            </>
          }
        >
          <Div>
            For your security, you will be logged out
            <br /> automatically when the timer below ends.
            <div className="timer">{formatTime(countdown * 1000)}</div>
          </Div>
        </ObModal>
      )}
    </>
  )
}

export default AutoLogout

const Div = styled.div`
  ${({ theme }) => theme.typography.m.normal};
  color: ${({ theme }) => theme.colors.neutral.neutral};
  line-height: 24px;
  .timer {
    ${({ theme }) => theme.typography.h6.bold};
    color: ${({ theme }) => theme.colors.neutral.neutral};
    text-align: center;
    margin-top: 8px;
  }
`
